import { useRef, useState, useEffect, useCallback, memo } from "react";
import { Upload, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  validateComprobanteFile,
  COMPROBANTE_ACCEPT,
  DEFAULT_MAX_SIZE_MB,
} from "@/shared/utils/fileValidation";

type FileUploadProps = {
  value: File | null;
  onChange: (file: File | null) => void;
  accept?: string;
  maxSizeMB?: number;
  label?: string;
  description?: string;
  /** Instruction when idle (e.g. "Click to upload or drag here"). When not provided, uses default in Spanish. */
  instructionText?: string;
  /** Instruction when dragging over (e.g. "Drop the file here"). When not provided, uses default in Spanish. */
  instructionTextDrop?: string;
  /** Second line under description (e.g. "Max 10 MB • Images (JPG, PNG, WebP)"). When not provided, uses default in Spanish. */
  sizeLine?: string;
  /** Alt text for preview image. When not provided, uses default in Spanish. */
  previewAlt?: string;
  /** "Change" button label. When not provided, uses default in Spanish. */
  changeLabel?: string;
  /** "Remove" button label. When not provided, uses default in Spanish. */
  removeLabel?: string;
  error?: string;
  disabled?: boolean;
  inputId?: string;
};

function FileUploadInner({
  value,
  onChange,
  accept = COMPROBANTE_ACCEPT,
  maxSizeMB = DEFAULT_MAX_SIZE_MB,
  label = "Comprobante (imagen)",
  description = "PNG, JPG, WebP hasta 10 MB",
  instructionText,
  instructionTextDrop,
  sizeLine,
  previewAlt,
  changeLabel,
  removeLabel,
  error: externalError,
  disabled = false,
  inputId = "file-upload-input",
}: FileUploadProps) {
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const displayError = validationError ?? externalError ?? null;

  useEffect(() => {
    if (!value || !value.type.startsWith("image/")) {
      const id = requestAnimationFrame(() => {
        setPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
      });
      return () => cancelAnimationFrame(id);
    }
    const url = URL.createObjectURL(value);
    const id = requestAnimationFrame(() => setPreviewUrl(url));
    return () => {
      cancelAnimationFrame(id);
      URL.revokeObjectURL(url);
    };
  }, [value]);

  const validateAndSet = useCallback(
    (file: File | null) => {
      if (!file) {
        setValidationError(null);
        onChange(null);
        return;
      }
      const result = validateComprobanteFile(file, maxSizeMB);
      if (!result.valid) {
        setValidationError(result.error);
        if (inputRef.current) inputRef.current.value = "";
        return;
      }
      setValidationError(null);
      onChange(file);
    },
    [maxSizeMB, onChange]
  );

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (disabled || !e.dataTransfer.files?.length) return;
      const file = e.dataTransfer.files[0];
      validateAndSet(file);
    },
    [disabled, validateAndSet]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      const file = e.target.files?.[0] ?? null;
      validateAndSet(file);
    },
    [disabled, validateAndSet]
  );

  const removeFile = useCallback(() => {
    if (disabled) return;
    setValidationError(null);
    onChange(null);
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.files = null;
    }
  }, [disabled, onChange]);

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
          {label}
        </label>
      )}

      <input
        ref={inputRef}
        type="file"
        id={inputId}
        accept={accept}
        disabled={disabled}
        onChange={handleChange}
        className="sr-only"
      />

      {!value ? (
        <div
          role="button"
          tabIndex={0}
          onClick={() => !disabled && inputRef.current?.click()}
          onKeyDown={(e) => {
            if (disabled) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={cn(
            "border-2 border-dashed rounded-xl px-6 py-10 flex flex-col items-center justify-center space-y-3 transition-all cursor-pointer group",
            disabled && "opacity-50 cursor-not-allowed",
            !disabled && "hover:bg-gray-100 dark:hover:bg-gray-800",
            dragActive && !disabled && "border-green-400 dark:border-green-500 bg-green-50/50 dark:bg-green-500/10 scale-[1.01]",
            displayError && "border-red-300 dark:border-red-600 bg-red-50/30 dark:bg-red-900/20",
            !displayError && !dragActive && "border-gray-300 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50"
          )}
        >
          <div
            className={cn(
              "p-3 rounded-full transition-transform group-hover:scale-110",
              dragActive ? "bg-green-500/20" : "bg-green-500/10"
            )}
          >
            <Upload className="h-8 w-8 text-green-500 dark:text-green-400" />
          </div>
          <div className="text-center space-y-1">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {dragActive
                ? (instructionTextDrop ?? "Suelta el archivo aquí")
                : (instructionText ?? "Haz clic para subir o arrastra aquí")}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500">
              {description}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500">
              {sizeLine ?? `Máximo ${maxSizeMB} MB • Imágenes (JPG, PNG, WebP)`}
            </p>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-50/50 dark:bg-gray-900/50">
          {previewUrl && value.type.startsWith("image/") && (
            <img
              src={previewUrl}
              alt={previewAlt ?? "Vista previa del comprobante"}
              className="w-full h-40 object-contain bg-white dark:bg-zinc-900"
            />
          )}
          <p className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 truncate">
            {value.name}
          </p>
          <div className="flex gap-2 p-3 border-t border-gray-100 dark:border-gray-800">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={disabled}
              className={cn(
                "flex-1 text-center text-sm font-medium text-green-600 dark:text-green-400 hover:underline",
                disabled && "opacity-50 cursor-not-allowed"
              )}
            >
              {changeLabel ?? "Cambiar"}
            </button>
            <button
              type="button"
              onClick={removeFile}
              disabled={disabled}
              className="flex items-center gap-1 text-sm font-medium text-rose-500 hover:text-rose-600 dark:hover:text-rose-400 disabled:opacity-50"
            >
              <X className="h-4 w-4" />
              {removeLabel ?? "Quitar"}
            </button>
          </div>
        </div>
      )}

      {displayError && (
        <p className="text-sm text-red-500 dark:text-red-400" role="alert">
          {displayError}
        </p>
      )}
    </div>
  );
}

export const FileUpload = memo(FileUploadInner);
